<template>
  <div class="h-100" v-if="location">
    <Splide
      v-if="!$vuetify.display.mobile"
      :options="{ rewind: true, autoplay: true }"
      :has-track="false"
      :aria-label="'Bildergalerie – Schreiner ' + location.name + ' – Schreinerei M²'"
      id="splide"
    >
      <SplideTrack class="d-flex flex-column">
        <SplideSlide
          v-for="(image, idx) in location.images"
          :key="image"
          class="d-flex flex-column images"
        >
          <v-img
            :lazy-src="getImageLowres(image)"
            transition="true"
            :alt="imageAlts[idx] || ('Schreinerei M² – Projekt in ' + location.name)"
            cover
            id="sliderimage"
            :src="getImage(image)"
          ></v-img>
        </SplideSlide>
      </SplideTrack>
    </Splide>
    <v-img
      v-else
      cover
      class="mr-md-8 mr-2"
      id="mobileInitImage"
      :lazy-src="getImageLowres(location.images[0])"
      transition="true"
      :alt="'Schreiner ' + location.name + ' – Schreinerei M² aus Pfullingen'"
      :src="getImage(location.images[0])"
    ></v-img>

    <v-card class="bg-primary pa-5 ml-md-8 ml-2 my-8" id="title">
      <h1 id="name" class="float-right pl-3 text-accent">{{ location.h1 }}</h1>
    </v-card>

    <v-card class="bg-secondary pa-5 mr-md-8 mr-2 my-8" id="description">
      <p class="font">{{ location.intro }}</p>
    </v-card>

    <section>
      <v-card class="bg-accent pa-5 ml-md-8 ml-2 my-8" id="services-card">
        <h2 class="mb-3">Unsere Leistungen für {{ location.name }}</h2>
        <p class="font">{{ location.services }}</p>
        <v-row class="mt-4">
          <v-col cols="12" sm="6">
            <ul class="font service-list">
              <li>Maßgefertigte Massivholzmöbel</li>
              <li>Individuelle Einbauschränke & Garderoben</li>
              <li>Küchen nach Maß</li>
              <li>Badmöbel & Waschtische</li>
            </ul>
          </v-col>
          <v-col cols="12" sm="6">
            <ul class="font service-list">
              <li>Kompletter Innenausbau</li>
              <li>Hotellerie & Gastronomie</li>
              <li>Ladenbau & Objektplanung</li>
              <li>Industrieklettern & Seilzugangstechnik</li>
            </ul>
          </v-col>
        </v-row>
      </v-card>
    </section>

    <section>
      <v-card class="bg-secondary pa-5 mr-md-8 mr-2 my-8" id="local-card">
        <h2 class="mb-3">Ihr Tischler vor Ort in {{ location.name }}</h2>
        <p class="font">{{ location.local }}</p>
      </v-card>
    </section>

    <section>
      <v-card class="bg-accent pa-5 ml-md-8 ml-2 my-8" id="why-card">
        <h2 class="mb-3">Warum Schreinerei M² für {{ location.name }}?</h2>
        <v-row>
          <v-col cols="12" sm="6" md="3" v-for="reason in reasons" :key="reason.title">
            <v-icon class="mb-2" size="32">{{ reason.icon }}</v-icon>
            <h3 class="font reason-title">{{ reason.title }}</h3>
            <p class="font text-medium-emphasis">{{ reason.text }}</p>
          </v-col>
        </v-row>
      </v-card>
    </section>

    <v-card class="bg-primary pa-5 mr-md-8 mr-2 my-8" id="cta-card">
      <v-row align="center" justify="center">
        <v-col cols="12" class="text-center">
          <h2 class="text-accent mb-3">Schreiner in {{ location.name }} gesucht? Sprechen Sie uns an!</h2>
          <p class="font text-accent mb-4">
            Rufen Sie uns direkt an oder schreiben Sie uns – wir beraten Sie gerne
            und kommen für ein unverbindliches Aufmaß zu Ihnen nach {{ location.name }}.
          </p>
          <div class="d-flex flex-column flex-md-row justify-center align-center">
            <v-btn
              size="large"
              class="bg-accent text-primary ma-2"
              href="tel:+4915778204841"
            >
              <v-icon start>mdi-phone</v-icon>
              +49 1577 8204841
            </v-btn>
            <v-btn
              size="large"
              class="bg-accent text-primary ma-2"
              to="/contact"
            >
              <v-icon start>mdi-email</v-icon>
              Jetzt Kontakt aufnehmen
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card>

    <v-card class="bg-secondary pa-5 ml-md-8 ml-2 mb-8" id="links-card">
      <p class="font">
        Erfahren Sie mehr über unser
        <router-link to="/einzugsgebiet" class="text-link">gesamtes Einzugsgebiet</router-link>,
        sehen Sie sich unsere
        <router-link to="/products" class="text-link">bisherigen Arbeiten</router-link> an
        oder lernen Sie
        <router-link to="/about" class="text-link">unser Team</router-link> kennen.
      </p>
    </v-card>
  </div>
</template>

<script>
import { Splide, SplideSlide, SplideTrack } from "@splidejs/vue-splide";
import "@splidejs/vue-splide/css";
import locationData from "../locationData.json";

export default {
  name: "LocationLanding",
  components: {
    Splide,
    SplideSlide,
    SplideTrack,
  },
  computed: {
    location() {
      const slug = this.$route.params.slug;
      return locationData.find((l) => l.slug === slug);
    },
    imageAlts() {
      if (!this.location) return [];
      const name = this.location.name;
      return [
        "Massivholzmöbel vom Schreiner – " + name,
        "Schreinerei M² – Handwerkskunst für " + name,
        "Individueller Innenausbau in " + name,
        "Meisterhafte Holzarbeiten für " + name,
        "Nachhaltige Möbel – Schreinerei " + name,
      ];
    },
  },
  data() {
    return {
      reasons: [
        {
          icon: "mdi-map-marker-radius",
          title: "Kurze Wege",
          text: "Persönliche Beratung und schnelle Montage durch regionale Nähe.",
        },
        {
          icon: "mdi-leaf",
          title: "Nachhaltig",
          text: "Zertifizierte Materialien aus nachhaltiger Produktion.",
        },
        {
          icon: "mdi-ruler-square-compass",
          title: "Maßarbeit",
          text: "Jedes Stück individuell geplant und passgenau gefertigt.",
        },
        {
          icon: "mdi-handshake",
          title: "Alles aus einer Hand",
          text: "Von der Beratung über die Fertigung bis zur Montage.",
        },
      ],
    };
  },
  watch: {
    location: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.$router.replace("/");
          return;
        }
        this.updateMeta(val);
      },
    },
  },
  methods: {
    updateMeta(loc) {
      if (loc && loc.metaDescription) {
        let metaTag = document.querySelector('meta[name="description"]');
        if (metaTag) {
          metaTag.setAttribute("content", loc.metaDescription);
        }
        let ogTag = document.querySelector('meta[property="og:description"]');
        if (ogTag) {
          ogTag.setAttribute("content", loc.metaDescription);
        }
      }
    },
    getImage(name) {
      return require("../assets/images/" + name + ".jpg");
    },
    getImageLowres(name) {
      return require("../assets/images/" + name + "_lowres.jpg");
    },
  },
};
</script>

<style scoped>
#splide {
  left: 0;
  min-width: 100%;
  max-height: 65vh;
  overflow: clip;
  display: flex;
  flex-direction: column-reverse;
}

@media (max-width: 959px) {
  #splide {
    max-height: 50vh;
  }
}

#sawSpinner {
  min-width: 200px;
  animation-name: saw;
  animation-duration: 4s;
  animation-iteration-count: infinite;
  animation-timing-function: ease;
}
@keyframes saw {
  0% {
    left: 0px;
    top: 0px;
    transform: rotate(0deg);
  }
  100% {
    left: 0px;
    top: 0px;
    transform: rotate(-720deg);
  }
}

.images {
  bottom: 0;
  right: 0;
}

#sliderimage {
  right: 0;
  bottom: 0;
}

#mobileInitImage {
  height: 50vh;
  border-radius: 0 0 15px 0;
}

#name {
  font-size: clamp(1.5em, 5vw, 3em);
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
}

h2 {
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
}

.font {
  font-size: 1em;
  font-family: "ManropeLight", Arial, Helvetica, sans-serif;
}

#title {
  border-radius: 15px 0px 0px 15px;
}

#description {
  border-radius: 0px 15px 15px 0px;
}

#services-card {
  border-radius: 15px 0px 0px 15px;
}

#local-card {
  border-radius: 0px 15px 15px 0px;
}

#why-card {
  border-radius: 15px 0px 0px 15px;
}

#cta-card {
  border-radius: 0px 15px 15px 0px;
}

#links-card {
  border-radius: 15px 0px 0px 15px;
}

.service-list {
  padding-left: 1.2em;
  line-height: 2;
}

.reason-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.text-link {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.text-link:hover {
  opacity: 0.8;
}
</style>
